import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useVerificationDetail,
  useApproveVerification,
  useRejectVerification,
  useRequestMoreInfo,
} from '@theraply/api-client';
import DocumentViewer from '../../components/DocumentViewer';
import VerificationActions from '../../components/VerificationActions';

export default function VerificationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const { data: verification, isLoading, error } = useVerificationDetail(id!);
  const approveMutation = useApproveVerification();
  const rejectMutation = useRejectVerification();
  const requestInfoMutation = useRequestMoreInfo();

  const handleApprove = async (note?: string) => {
    if (!id) return;

    await approveMutation.mutateAsync({ id, data: { note } });
    navigate('/verifications');
  };

  const handleReject = async (reason: string, note?: string) => {
    if (!id) return;

    await rejectMutation.mutateAsync({ id, data: { reason, note } });
    navigate('/verifications');
  };

  const handleRequestInfo = async (message: string) => {
    if (!id) return;

    await requestInfoMutation.mutateAsync({ id, data: { message } });
    navigate('/verifications');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';

    switch (status) {
      case 'PENDING':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending Review</span>;
      case 'APPROVED':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Approved</span>;
      case 'REJECTED':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Rejected</span>;
      case 'NEEDS_INFO':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Needs More Info</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const getDocumentStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';

    switch (status) {
      case 'UPLOADED':
        return <span className={`${baseClasses} bg-green-100 text-green-700`}>Uploaded</span>;
      case 'PROCESSING':
        return <span className={`${baseClasses} bg-blue-100 text-blue-700`}>Processing</span>;
      case 'PROCESSED':
        return <span className={`${baseClasses} bg-green-100 text-green-700`}>Processed</span>;
      case 'FAILED':
        return <span className={`${baseClasses} bg-red-100 text-red-700`}>Failed</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-700`}>{status}</span>;
    }
  };

  const selectedDocument = verification?.documents.find((doc) => doc.id === selectedDocumentId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-sm text-gray-500">Loading verification details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading verification</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!verification) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-sm text-gray-500">Verification not found</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/verifications')}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-4"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Verifications
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {verification.firstName} {verification.lastName}
            </h1>
            <p className="mt-1 text-sm text-gray-500">{verification.email}</p>
          </div>
          <div>{getStatusBadge(verification.verificationStatus)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Therapist Info */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Therapist Information</h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {verification.firstName} {verification.lastName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{verification.email}</dd>
                </div>
                {verification.phone && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{verification.phone}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Submitted</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(verification.submittedAt)}</dd>
                </div>
                {verification.bio && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Bio</dt>
                    <dd className="mt-1 text-sm text-gray-900">{verification.bio}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Professional Info */}
          {verification.professionalInfo && Object.keys(verification.professionalInfo).length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Professional Information</h3>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  {verification.professionalInfo.licenseType && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">License Type</dt>
                      <dd className="mt-1 text-sm text-gray-900">{verification.professionalInfo.licenseType}</dd>
                    </div>
                  )}
                  {verification.professionalInfo.licenseState && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">License State</dt>
                      <dd className="mt-1 text-sm text-gray-900">{verification.professionalInfo.licenseState}</dd>
                    </div>
                  )}
                  {verification.professionalInfo.licenseNumber && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">License Number</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{verification.professionalInfo.licenseNumber}</dd>
                    </div>
                  )}
                  {verification.professionalInfo.npiNumber && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">NPI Number</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{verification.professionalInfo.npiNumber}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Documents ({verification.documents.length})
              </h3>
              <div className="space-y-4">
                {verification.documents.length === 0 ? (
                  <p className="text-sm text-gray-500">No documents uploaded</p>
                ) : (
                  verification.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedDocumentId(doc.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900">{doc.documentType.replace(/_/g, ' ')}</h4>
                            {getDocumentStatusBadge(doc.status)}
                          </div>
                          <p className="text-sm text-gray-500 truncate">{doc.fileName}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Uploaded {formatDate(doc.uploadedAt)} • {(doc.fileSize / 1024).toFixed(1)} KB
                          </p>
                          {doc.extractedData && (
                            <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-100">
                              <p className="text-xs font-medium text-blue-900 mb-2">Extracted Data:</p>
                              <dl className="grid grid-cols-2 gap-2 text-xs">
                                {Object.entries(doc.extractedData).map(([key, value]) => (
                                  <div key={key}>
                                    <dt className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</dt>
                                    <dd className="text-gray-900 font-medium">{String(value)}</dd>
                                  </div>
                                ))}
                              </dl>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocumentId(doc.id);
                          }}
                          className="ml-4 text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Verification History */}
          {verification.history.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Verification History</h3>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {verification.history.map((item, index) => (
                      <li key={item.id}>
                        <div className="relative pb-8">
                          {index !== verification.history.length - 1 && (
                            <span
                              className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          )}
                          <div className="relative flex items-start space-x-3">
                            <div className="relative">
                              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                                <span className="text-sm font-medium text-gray-600">
                                  {item.admin.firstName[0]}{item.admin.lastName[0]}
                                </span>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div>
                                <div className="text-sm">
                                  <span className="font-medium text-gray-900">
                                    {item.admin.firstName} {item.admin.lastName}
                                  </span>
                                </div>
                                <p className="mt-0.5 text-xs text-gray-500">{formatDate(item.createdAt)}</p>
                              </div>
                              <div className="mt-2 text-sm text-gray-700">
                                <p className="font-medium text-gray-900">{item.action}</p>
                                <p className="mt-1">{item.note}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Actions */}
        <div className="lg:col-span-1">
          <VerificationActions
            verification={verification}
            onApprove={handleApprove}
            onReject={handleReject}
            onRequestInfo={handleRequestInfo}
            isSubmitting={
              approveMutation.isPending || rejectMutation.isPending || requestInfoMutation.isPending
            }
          />
        </div>
      </div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          onClose={() => setSelectedDocumentId(null)}
        />
      )}
    </div>
  );
}
